import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Check, MessageCircle } from "lucide-react";
import { SectionHeading, Reveal, whatsappFor, WHATSAPP_URL } from "./shared";

const IMG = {
  tela: "/servico-tela.jpg",
  bateria: "/servico-bateria.jpg",
  conector: "/servico-conector.jpg",
  placa: "/servico-placa.jpg",
  software: "/servico-software.jpg",
};

const SERVICES = [
  {
    id: "troca-de-tela",
    code: "SRV-01",
    title: "Troca de Tela & Display",
    badge: "O Mais Pedido",
    summary:
      "Tela nova com cores e brilho calibrados, biometria e touch funcionando perfeitamente. Peças de qualidade e garantia escrita.",
    specs: ["OLED / Super Retina / AMOLED", "Calibração de brilho e cor", "Touch testado ponto a ponto"],
    img: IMG.tela,
    brands: ["iphone", "samsung", "xiaomi", "motorola"],
  },
  {
    id: "bateria",
    code: "SRV-02",
    title: "Troca de Bateria",
    badge: "Zero Ciclos",
    summary:
      "Baterias testadas em bancada para devolver a saúde do aparelho, sem aviso de bateria desconhecida no sistema.",
    specs: ["Células homologadas Anatel", "Sem erro no iOS/Android", "Testadas em bancada"],
    img: IMG.bateria,
    brands: ["iphone", "samsung", "xiaomi", "motorola"],
  },
  {
    id: "conector-de-carga",
    code: "SRV-03",
    title: "Conector de Carga USB-C / Lightning",
    badge: "Solda de Precisão",
    summary:
      "Solução definitiva para mau contato, carga lenta ou celular que não carrega. Troca do conector e limpeza completa.",
    specs: ["Limpeza ultra-sônica", "Teste de carga rápida", "Microfone inferior novo"],
    img: IMG.conector,
    brands: ["iphone", "samsung", "xiaomi", "motorola"],
  },
  {
    id: "reparo-de-placa",
    code: "SRV-04",
    title: "Reparo de Placa & Microsoldagem",
    badge: "Especialidade da Casa",
    summary:
      "Curto na placa, Face ID com defeito e danos por água: reparamos o componente certo, sem trocar a placa inteira.",
    specs: ["Diagnóstico com microscópio", "Reparo do componente em falha", "Laudo com fotos"],
    img: IMG.placa,
    brands: ["iphone", "samsung", "xiaomi", "motorola"],
  },
  {
    id: "software",
    code: "SRV-05",
    title: "Software & Recuperação de Sistema",
    badge: "Sistema Limpo",
    summary:
      "Celular travado na tela de logo, lento ou com vírus? Deixamos o sistema limpo, atualizado e rápido de novo.",
    specs: ["Travamentos iOS & Android", "Remoção de vírus e lentidão", "Sistema limpo e atualizado"],
    img: IMG.software,
    brands: ["iphone", "samsung", "xiaomi", "motorola"],
  },
];

const BRAND_TABS = [
  { id: "todos", label: "Todos" },
  { id: "iphone", label: "iPhone" },
  { id: "samsung", label: "Samsung Galaxy" },
  { id: "xiaomi", label: "Xiaomi" },
  { id: "motorola", label: "Motorola" },
];

const ServiceCard = ({ s, large }) => (
  <motion.article
    layout
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.96 }}
    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
    data-testid={`service-card-${s.id}`}
    className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-[#2E373E] hover:border-[#99BA4B]/60 hover:shadow-[0_0_30px_rgba(153,186,75,0.15)] hover:-translate-y-1 transition-all duration-300 ${
      large ? "lg:col-span-4" : "lg:col-span-2"
    }`}
  >
    <div className={`relative overflow-hidden ${large ? "h-52" : "h-36"}`}>
      <img
        src={s.img}
        alt={s.title}
        loading="lazy"
        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#2E373E] via-transparent to-transparent" />
      <span className="absolute top-3 left-3 font-mono-tech text-[10px] tracking-[0.2em] bg-black/60 backdrop-blur-sm border border-white/15 text-zinc-200 px-2.5 py-1 rounded-full">
        {s.code}
      </span>
      <span className="absolute top-3 right-3 font-mono-tech text-[10px] tracking-wide bg-[#99BA4B] text-[#1D2328] font-bold px-2.5 py-1 rounded-full">
        {s.badge}
      </span>
    </div>

    <div className="p-5 sm:p-6">
      <h3 className="font-display text-lg sm:text-xl text-white leading-tight">{s.title}</h3>
      <p className="mt-2.5 text-sm leading-relaxed text-zinc-300">{s.summary}</p>
      <ul className="mt-4 space-y-1.5">
        {s.specs.map((spec) => (
          <li key={spec} className="flex items-start gap-2 text-xs text-zinc-300">
            <Check size={13} className="text-[#ADCF5D] mt-0.5 shrink-0" />
            {spec}
          </li>
        ))}
      </ul>
      <div className="mt-5 flex items-center justify-between gap-3">
        <span className="flex items-center gap-1.5 font-mono-tech text-[11px] text-zinc-400">
          <ShieldCheck size={12} className="text-[#ADCF5D]" />
          Garantia de 6 meses
        </span>
        <a
          data-testid={`service-whatsapp-button-${s.id}`}
          href={whatsappFor(s.title)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-[#ADCF5D] hover:text-[#99BA4B] transition-colors"
        >
          <MessageCircle size={14} />
          Orçamento
        </a>
      </div>
    </div>
  </motion.article>
);

export default function Services() {
  const [brand, setBrand] = useState("todos");
  const filtered =
    brand === "todos" ? SERVICES : SERVICES.filter((s) => s.brands.includes(brand));

  return (
    <section id="servicos" className="relative py-24 sm:py-32 px-4 sm:px-8">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          overline="/// Nossos Serviços"
          title="SEU CELULAR MERECE CUIDADO DE VERDADE"
          subtitle="Escolha o serviço abaixo ou traga o aparelho para uma avaliação. Todos os reparos têm garantia de 6 meses documentada."
        />

        <Reveal delay={0.1} className="mt-10">
          <div
            className="flex flex-wrap gap-2"
            role="tablist"
            aria-label="Filtrar por marca"
            data-testid="interactive-service-modal-or-tab"
          >
            {BRAND_TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={brand === t.id}
                data-testid={`service-brand-tab-${t.id}`}
                onClick={() => setBrand(t.id)}
                className={`font-mono-tech text-[11px] tracking-[0.15em] uppercase px-4 py-2 rounded-full border transition-all duration-200 active:scale-95 ${
                  brand === t.id
                    ? "bg-[#99BA4B] text-[#1D2328] border-[#99BA4B] font-bold"
                    : "border-white/15 text-zinc-300 hover:border-[#99BA4B]/60 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-5">
          <AnimatePresence mode="popLayout">
            {filtered.map((s, i) => (
              <ServiceCard key={s.id} s={s} large={i === 0 && filtered.length > 3} />
            ))}
          </AnimatePresence>
        </motion.div>

        <Reveal delay={0.1} className="mt-10 text-center">
          <a
            data-testid="service-whatsapp-button"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 border border-[#99BA4B]/50 hover:bg-[#99BA4B]/10 text-[#ADCF5D] font-bold px-7 py-3.5 rounded-full text-sm transition-all duration-150 active:scale-95"
          >
            <MessageCircle size={17} />
            Não achou seu defeito? Fale com a gente agora
          </a>
        </Reveal>
      </div>
    </section>
  );
}
